import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { Discount } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { useDiscountScope } from '../hooks/use-discount-scope';
import { useDiscountValue } from '../hooks/use-discount-value';

import { DiscountKindBadge, DiscountStateBadge } from './discount-badges';

/** Column model of the discounts table; `meta` ties columns to the list's sorts and filters. */
export function useDiscountColumns(storeId: string) {
  const { t } = useTranslation('discounts');
  const value = useDiscountValue();
  const scope = useDiscountScope();
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<Discount>>>(
    () => [
      {
        id: 'title',
        header: t('columns.title'),
        cell: ({ row }) => (
          <Link
            to="/s/$storeId/discounts/$discountId"
            params={{ storeId, discountId: row.original.id }}
            className="font-medium hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.title}
          </Link>
        ),
        meta: { label: t('columns.title'), sortField: 'title', lockVisibility: true },
      },
      {
        id: 'kind',
        header: t('columns.kind'),
        cell: ({ row }) => <DiscountKindBadge kind={row.original.kind} />,
        meta: { label: t('columns.kind'), filterKey: 'kind' },
        size: 110,
      },
      {
        id: 'value',
        header: t('columns.value'),
        cell: ({ row }) => <span className="tabular-nums">{value(row.original)}</span>,
        meta: { label: t('columns.value') },
      },
      {
        id: 'scope',
        header: t('columns.scope'),
        cell: ({ row }) => <span className="text-muted-foreground">{scope(row.original)}</span>,
        meta: { label: t('columns.scope'), filterKey: 'scope' },
      },
      {
        id: 'codes',
        header: t('columns.codes'),
        cell: ({ row }) => {
          const { kind, codes } = row.original;
          if (kind === 'automatic') {
            return <span className="text-muted-foreground">{t('kind.automatic')}</span>;
          }
          const [first] = codes;
          return codes.length === 1 && first ? (
            <code className="text-[13px]">{first.code}</code>
          ) : (
            t('codes.count', { count: codes.length })
          );
        },
        meta: { label: t('columns.codes') },
      },
      {
        id: 'startsAt',
        header: t('columns.startsAt'),
        cell: ({ row }) =>
          row.original.startsAt ? (
            <span className="text-muted-foreground" title={format.utc(row.original.startsAt)}>
              {format.date(row.original.startsAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('columns.noStart')}</span>
          ),
        meta: { label: t('columns.startsAt'), sortField: 'startsAt', filterKey: 'startsAt' },
        size: 120,
      },
      {
        id: 'usage',
        header: t('columns.usage'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.usageLimitTotal === null
              ? row.original.usageCount
              : t('usage.ofLimit', {
                  count: row.original.usageCount,
                  limit: row.original.usageLimitTotal,
                })}
          </span>
        ),
        meta: { label: t('columns.usage'), sortField: 'usageCount', align: 'end' },
        size: 100,
      },
      {
        id: 'state',
        header: t('columns.state'),
        cell: ({ row }) => <DiscountStateBadge discount={row.original} />,
        meta: { label: t('columns.state'), filterKey: 'status' },
        size: 110,
      },
    ],
    [format, scope, storeId, t, value],
  );
}
